import cn from 'classnames';
import { noop } from 'lodash';
import { type MemoExoticComponent, memo, type ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { NegativeMood } from './assets/NegativeMood';
import { NeutralMood } from './assets/NeutralMood';
import { PositiveMood } from './assets/PositiveMood';
import { MOOD_STATES, MOODS } from './constants';
import messages from './MoodBox.messages';
import * as styles from './MoodBox.styles';

export type MoodBoxProps = {
  mood: MOODS;
  state: MOOD_STATES;
  onClick?: (mood: MOODS) => void;
};

const moodBoxMap = (state: MOOD_STATES): Record<MOODS, ReactElement> => ({
  [MOODS.NEGATIVE]: <NegativeMood state={state} />,
  [MOODS.NEUTRAL]: <NeutralMood state={state} />,
  [MOODS.POSITIVE]: <PositiveMood state={state} />,
});

export const MoodBoxBase = ({ mood, onClick = noop, state }: MoodBoxProps) => {
  const intl = useIntl();

  return (
    <button
      className={cn(mood, state)}
      onClick={() => onClick(mood)}
      aria-label={intl.formatMessage(messages[mood])}
      css={styles.moodBoxContainer}
      type="button"
    >
      {moodBoxMap(state)[mood]}
    </button>
  );
};

MoodBoxBase.displayName = 'MoodBox';

type CompoundedComponent = MemoExoticComponent<typeof MoodBoxBase> & {
  MOODS: typeof MOODS;
  MOOD_STATES: typeof MOOD_STATES;
};

const MoodBox = memo(MoodBoxBase) as CompoundedComponent;

MoodBox.MOODS = MOODS;
MoodBox.MOOD_STATES = MOOD_STATES;

export { MoodBox };
